import React, { useState, useEffect } from "react";
import { Truck, Tag, ChevronDown, ChevronUp, X } from "lucide-react";

const DiscountModal = ({ isOpen, onClose, message, type }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm shadow-lg w-full max-w-md mx-4 overflow-hidden">
        <div className={`${type === "success" ? "bg-[#D4AF37]" : "bg-red-500"} px-4 py-3 flex justify-between items-center`}>
          <h3 className="text-white font-medium">{type === "success" ? "Success" : "Error"}</h3>
          <button onClick={onClose} className="text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700">{message}</p>
          <div className="mt-4 flex justify-end">
            <button 
              onClick={onClose} 
              className="px-4 py-2 bg-[#D4AF37] text-white rounded-sm hover:bg-[#C19B2E] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderSummary = ({ cartItems, hasCartItems, subtotal, shippingCost, total, onDiscountUpdate }) => {
  const [couponCode, setCouponCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState({ text: "", type: "" });
  const [showCouponField, setShowCouponField] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Effect to update parent component when discount changes
  useEffect(() => {
    if (typeof onDiscountUpdate === 'function') {
      onDiscountUpdate(appliedDiscount);
    }
  }, [appliedDiscount, onDiscountUpdate]);

  const calculateDiscountAmount = (discountData, subtotalAmount) => {
    const { type, value } = discountData;
    
    if (type === 'Percentage') {
      // Handle percentage discount
      const percentage = parseInt(value.replace('%', ''));
      if (!isNaN(percentage)) {
        return subtotalAmount * (percentage / 100);
      }
    } else if (type === 'Fixed') {
      // Handle fixed discount in rupees (remove ₹ or $ symbol and convert to number)
      const fixedAmount = parseFloat(value.replace(/[₹$]/g, ''));
      if (!isNaN(fixedAmount)) {
        return fixedAmount;
      }
    }
    
    return 0;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setModalMessage({ text: "Please enter a coupon code", type: "error" });
      setModalOpen(true);
      return;
    }

    setIsVerifying(true);
    try {
      // Call the API to verify the coupon
      const response = await fetch(`${API_BASE_URL}/verify-discount/?discount_code=${couponCode}`);
      const data = await response.json();
      
      if (response.ok) {
        // Calculate discount amount based on type
        const discountAmount = calculateDiscountAmount(data.discount, subtotal);
        
        if (discountAmount > 0) {
          // Ensure discount doesn't exceed subtotal
          const finalDiscountAmount = Math.min(discountAmount, subtotal);
          
          setAppliedDiscount(finalDiscountAmount);
          setDiscountInfo(data.discount);
          
          const discountText = data.discount.type === 'Percentage' 
            ? `${data.discount.value} discount`
            : `₹${data.discount.value.replace(/[₹$]/g, '')} off`;
            
          setModalMessage({ 
            text: `${data.discount.name} (${discountText}) has been applied to your order!`, 
            type: "success" 
          });
        } else {
          setModalMessage({ 
            text: "Could not apply discount. Please try again.", 
            type: "error" 
          });
        }
      } else {
        setAppliedDiscount(0);
        setDiscountInfo(null);
        setModalMessage({ text: data.error || "Invalid coupon code", type: "error" });
      }
    } catch (error) {
      setAppliedDiscount(0);
      setDiscountInfo(null);
      setModalMessage({ text: "Error verifying coupon. Please try again.", type: "error" });
    } finally {
      setIsVerifying(false);
      setModalOpen(true);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setAppliedDiscount(0);
    setDiscountInfo(null);
    setModalMessage({ text: "Discount removed", type: "success" });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const finalTotal = total - appliedDiscount;

  return (
    <>
      <div className="lg:w-1/3 bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
        <h2 className="text-2xl text-[#333333] mb-4" style={{ fontFamily: "serif" }}>
          Order Summary
        </h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg text-[#333333] mb-2">Items</h3>
            {hasCartItems ? (
              cartItems.map((item) => (
                <div
                  key={item.cart_id}
                  className="flex justify-between text-sm text-gray-600 mb-2"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{item.name}</span>
                    <span className="text-xs text-gray-500">
                      Size: {item.size} | Qty: {item.quantity}
                    </span>
                  </div>
                  <span>₹{item.total_price}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">No items in cart</p>
            )}
          </div>

          {/* Coupon Section */}
          <div className="border-t pt-4">
            <button
              onClick={() => setShowCouponField(!showCouponField)}
              className="flex items-center text-sm text-[#D4AF37] hover:text-[#C19B2E] transition-colors"
            >
              {showCouponField ? (
                <ChevronUp className="w-4 h-4 mr-1" />
              ) : (
                <ChevronDown className="w-4 h-4 mr-1" />
              )}
              <Tag className="w-4 h-4 mr-2" />
              {appliedDiscount > 0 ? "Discount Applied" : "Have a coupon code?"}
            </button>

            {showCouponField && (
              <div className="mt-3">
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter coupon code"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm"
                    disabled={isVerifying}
                  />
                  {appliedDiscount > 0 ? (
                    <button
                      onClick={handleRemoveCoupon}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-sm text-sm hover:bg-gray-300 transition-colors"
                      disabled={isVerifying}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={handleApplyCoupon}
                      className="px-3 py-2 bg-[#D4AF37] text-black rounded-sm text-sm hover:bg-[#C19B2E] transition-colors"
                      disabled={isVerifying}
                    >
                      {isVerifying ? "Verifying..." : "Apply"}
                    </button>
                  )}
                </div>
                
                {discountInfo && (
                  <div className="mt-2 mb-3 text-xs bg-gray-50 p-2 border border-gray-200 rounded">
                    <p className="font-medium text-[#D4AF37]">{discountInfo.name}</p>
                    <p className="text-gray-600">
                      {discountInfo.type === 'Percentage' ? `${discountInfo.value} off` : `₹${discountInfo.value.replace(/[₹$]/g, '')} discount`}
                    </p>
                    <p className="text-gray-600">Valid until: {discountInfo.endDate}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>₹{subtotal}</span>
          </div>

          {appliedDiscount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount</span>
              <span>-₹{appliedDiscount.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm text-gray-600">
            <span>
              <Truck className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
              Shipping
            </span>
            <span>{shippingCost === 0 ? "Free" : `₹${shippingCost}`}</span>
          </div>

          <div className="flex justify-between text-lg font-medium text-[#333333] border-t pt-2">
            <span>Total</span>
            <span>₹{finalTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
      
      {/* Discount Modal */}
      <DiscountModal 
        isOpen={modalOpen}
        onClose={closeModal}
        message={modalMessage.text}
        type={modalMessage.type}
      />
    </>
  );
};

export default OrderSummary;